import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Loader2, Play, Lock, Download, Gift, ShoppingCart, Check, Share2,
  Clapperboard, MapPin, Calendar, X, Film, ChevronLeft, ChevronRight, Sparkles,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

// Thumbnail that plays the lightweight preview (muted, looped) on hover.
function ClipHoverMedia({ v, slug }) {
  const [hover, setHover] = useState(false);
  const [canPlay, setCanPlay] = useState(false);
  const videoRef = useRef(null);

  const onEnter = () => setHover(true);
  const onLeave = () => { setHover(false); setCanPlay(false); };

  // React doesn't reliably set the `muted` DOM property, which makes browsers
  // block autoplay. Force it imperatively so the hover preview actually plays.
  const startPlayback = (el) => {
    if (!el) return;
    el.muted = true;
    el.defaultMuted = true;
    const p = el.play();
    if (p && p.catch) p.catch(() => {});
  };

  return (
    <div className="absolute inset-0" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      {v.thumbnail_path ? (
        <img
          src={`${BACKEND}/api/files/${v.thumbnail_path}`}
          alt={v.title}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      ) : (
        <div className="absolute inset-0 grid place-items-center text-[#00F0FF]/40">
          <Film className="h-10 w-10" />
        </div>
      )}
      {hover && (
        <video
          ref={videoRef}
          data-testid={`hover-preview-${v.id}`}
          src={`${API}/vault/${slug}/videos/${v.id}/stream`}
          muted
          autoPlay
          loop
          playsInline
          preload="auto"
          onLoadedMetadata={(e) => startPlayback(e.target)}
          onCanPlay={(e) => { setCanPlay(true); startPlayback(e.target); }}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${canPlay ? "opacity-100" : "opacity-0"}`}
        />
      )}
    </div>
  );
}

export default function Vault() {
  const { slug } = useParams();
  const emailKey = `vault_email_${slug}`;
  const [email, setEmail] = useState(() => localStorage.getItem(emailKey) || "");
  const [emailInput, setEmailInput] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);
  const [pending, setPending] = useState(null); // fn(email) to run after email captured
  const [vault, setVault] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [player, setPlayer] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [cart, setCart] = useState(() => new Set());
  const [cartBusy, setCartBusy] = useState(false);

  const clientState = vault?.client_state;
  const unlockAll = !!vault?.unlock_all;
  const unlocked = new Set(clientState?.unlocked_video_ids || []);
  const remaining = clientState?.complimentary_remaining ?? 0;
  const compTotal = clientState?.complimentary_total ?? vault?.complimentary_downloads ?? 0;
  const compRemaining = clientState?.complimentary_remaining ?? compTotal;
  const totalClips = vault?.videos?.length ?? 0;
  const isFree = (v) => Number(v?.price || 0) === 0;
  const isOwned = (v) => unlockAll || unlocked.has(v.id);
  const isAccessible = (v) => isOwned(v) || isFree(v);
  const allUnlocked = totalClips > 0 && (vault?.videos || []).every(isAccessible);

  const load = useCallback(async () => {
    try {
      const url = email ? `/vault/${slug}?email=${encodeURIComponent(email)}` : `/vault/${slug}`;
      const { data } = await api.get(url);
      setVault(data);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [slug, email]);

  useEffect(() => { load(); }, [load]);

  const ensureEmail = (action) => {
    if (email) { action(email); return; }
    setPending(() => action);
    setEmailOpen(true);
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    const value = emailInput.toLowerCase();
    try {
      const { data } = await api.post(`/vault/${slug}/client`, { email: value });
      localStorage.setItem(emailKey, value);
      setEmail(value);
      setVault((prev) => (prev ? { ...prev, client_state: data } : prev));
      setEmailOpen(false);
      const action = pending;
      setPending(null);
      if (action) setTimeout(() => action(value), 50);
      else toast.success("You're all set");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openPlayer = async (v) => {
    setPlayer(v);
    try {
      await api.post(`/vault/${slug}/videos/${v.id}/view`);
      setVault((prev) => prev && {
        ...prev,
        videos: prev.videos.map((x) => (x.id === v.id ? { ...x, views: x.views + 1 } : x)),
      });
    } catch { /* ignore */ }
  };

  const playerIndex = player && vault ? vault.videos.findIndex((x) => x.id === player.id) : -1;
  const gotoPlayer = (dir) => {
    if (!vault || playerIndex < 0) return;
    const next = vault.videos[playerIndex + dir];
    if (next) openPlayer(next);
  };

  useEffect(() => {
    if (!player) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") gotoPlayer(-1);
      else if (e.key === "ArrowRight") gotoPlayer(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player, playerIndex, vault]);

  const unlockFree = (v) => ensureEmail(async (mail) => {
    setBusyId(v.id);
    try {
      const { data } = await api.post(`/vault/${slug}/videos/${v.id}/unlock`, { email: mail });
      setVault((prev) => ({ ...prev, client_state: data }));
      toast.success(`"${v.title}" unlocked with a free download`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  });

  const buy = (v) => ensureEmail(async (mail) => {
    setBusyId(v.id);
    try {
      const { data } = await api.post(`/vault/${slug}/videos/${v.id}/checkout`, {
        email: mail, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusyId(null);
    }
  });

  const download = (v) => ensureEmail((mail) => {
    const link = `${API}/vault/${slug}/videos/${v.id}/download?email=${encodeURIComponent(mail)}`;
    window.open(link, "_blank");
    toast.success("Downloading original file");
    setTimeout(load, 1500);
  });

  const downloadAll = () => ensureEmail((mail) => {
    const link = `${API}/vault/${slug}/download-all?email=${encodeURIComponent(mail)}`;
    window.open(link, "_blank");
    toast.success("Preparing your download…");
    setTimeout(load, 2000);
  });

  const purchaseAll = () => ensureEmail(async (mail) => {
    setBusyId("all");
    try {
      const { data } = await api.post(`/vault/${slug}/checkout-all`, {
        email: mail, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusyId(null);
    }
  });

  const share = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Vault link copied");
  };

  const inCart = (v) => cart.has(v.id);
  const toggleCart = (v) => {
    setCart((prev) => {
      const next = new Set(prev);
      if (next.has(v.id)) { next.delete(v.id); }
      else { next.add(v.id); toast.success(`Added "${v.title}" to cart`); }
      return next;
    });
  };
  const cartItems = (vault?.videos || []).filter((v) => cart.has(v.id) && !isAccessible(v));
  const cartTotal = cartItems.reduce((s, v) => s + Number(v.price || 0), 0);

  const checkoutCart = () => ensureEmail(async (mail) => {
    const ids = cartItems.map((v) => v.id);
    if (ids.length === 0) { toast.error("Your cart is empty"); return; }
    setCartBusy(true);
    try {
      const { data } = await api.post(`/vault/${slug}/checkout-selected`, {
        email: mail, origin_url: window.location.origin, video_ids: ids,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setCartBusy(false);
    }
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] grid place-items-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#00F0FF]" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#050505] grid place-items-center text-center px-6">
        <div>
          <Lock className="h-12 w-12 text-white/20 mx-auto" />
          <h1 className="font-display text-2xl font-bold mt-6">Vault unavailable</h1>
          <p className="text-white/50 mt-2">This link is invalid or the vault hasn't been published yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* On-demand email capture (only when unlocking / buying / downloading) */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="bg-[#0d0d0d] border-white/10 text-white">
          <DialogHeader>
            <span className="grid place-items-center h-12 w-12 rounded-xl bg-[#00F0FF] text-black mb-3">
              <Clapperboard className="h-6 w-6" />
            </span>
            <DialogTitle className="font-display text-2xl">One quick step</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-white/50 -mt-2">
            Enter your email so we can track your downloads and purchases for{" "}
            <span className="text-white">{vault?.title}</span>.
          </p>
          <form onSubmit={submitEmail} className="mt-4 space-y-3">
            <input
              data-testid="client-email-input"
              type="email"
              required
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="you@email.com"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#00F0FF]"
            />
            <button
              data-testid="client-email-submit"
              className="w-full bg-[#00F0FF] text-black font-semibold rounded-full py-3 hover:scale-[1.02] transition-transform duration-300"
            >
              Continue
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end grain overflow-hidden">
        {vault?.cover_image_path ? (
          <img src={`${BACKEND}/api/files/${vault.cover_image_path}`} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#141414] to-black" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/20" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-5 md:px-8 pb-14 pt-32">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-none max-w-3xl">
              {vault?.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-5 text-sm text-white/70">
              <span className="flex items-center gap-2">
                {vault?.creator?.picture ? (
                  <img src={vault.creator.picture} alt="" className="h-8 w-8 rounded-full object-cover ring-1 ring-white/20" />
                ) : (
                  <span className="grid place-items-center h-8 w-8 rounded-full bg-[#00F0FF]/20 text-[#00F0FF] text-xs font-semibold">
                    {(vault?.creator?.name || "C")[0].toUpperCase()}
                  </span>
                )}
                By {vault?.creator?.name || "Creator"}
              </span>
              {vault?.event_date && <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" />{vault.event_date}</span>}
              {vault?.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{vault.location}</span>}
              <span className="flex items-center gap-1.5"><Film className="h-4 w-4" />{vault?.videos.length} clips</span>
            </div>
            {vault?.description && (
              <p className="mt-5 text-white/60 max-w-2xl leading-relaxed">{vault.description}</p>
            )}
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                onClick={share}
                data-testid="share-vault-btn"
                className="inline-flex items-center gap-2 bg-white text-black font-semibold rounded-full px-6 py-3 hover:scale-105 transition-transform duration-300"
              >
                <Share2 className="h-4 w-4" /> Share
              </button>
              {allUnlocked ? (
                <button
                  onClick={downloadAll}
                  data-testid="download-all-btn"
                  className="inline-flex items-center gap-2 bg-[#FFDD03] text-black font-semibold rounded-full px-6 py-3 hover:scale-105 transition-transform duration-300 orange-glow"
                >
                  <Download className="h-4 w-4" /> Download all
                </button>
              ) : totalClips > 0 && (
                <button
                  onClick={purchaseAll}
                  disabled={busyId === "all"}
                  data-testid="purchase-all-btn"
                  className="inline-flex items-center gap-2 bg-[#00F0FF] text-black font-semibold rounded-full px-6 py-3 hover:scale-105 transition-transform duration-300 cyan-glow disabled:opacity-60"
                >
                  {busyId === "all" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
                  Purchase all
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-14">
        <div className="flex items-center justify-between mb-8 gap-4">
          <h2 className="font-display text-2xl md:text-3xl font-bold">All clips</h2>
          {compTotal > 0 && (
            <span
              data-testid="free-clips-remaining"
              className="inline-flex items-center gap-2 rounded-full bg-[#00F0FF]/15 text-[#00F0FF] border border-[#00F0FF]/30 px-4 py-1.5 text-xs backdrop-blur-md"
            >
              <Gift className="h-3.5 w-3.5" /> {compRemaining}/{compTotal} free clips remaining
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {vault?.videos.map((v, i) => {
            const owned = isOwned(v);
            const free = isFree(v);
            const canFree = !owned && !free && remaining > 0;
            return (
              <motion.div
                key={v.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: Math.min(i * 0.05, 0.4) }}
                data-testid={`clip-card-${v.id}`}
                className="group rounded-2xl border border-white/10 bg-[#0d0d0d] overflow-hidden hover:border-[#00F0FF]/40 transition-colors"
              >
                <button
                  onClick={() => openPlayer(v)}
                  data-testid={`play-clip-${v.id}`}
                  className="relative block w-full aspect-video overflow-hidden bg-gradient-to-br from-[#1a1a1a] to-black"
                >
                  <ClipHoverMedia v={v} slug={slug} />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors grid place-items-center pointer-events-none">
                    <span className={`grid place-items-center h-14 w-14 rounded-full text-black scale-90 group-hover:scale-100 transition-transform duration-300 ${owned ? "bg-[#FFDD03] orange-glow" : "bg-[#00F0FF] cyan-glow"}`}>
                      <Play className="h-6 w-6 ml-0.5" fill="currentColor" />
                    </span>
                  </div>
                  {owned ? (
                    <span data-testid={`tag-${v.id}`} className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-[#FFDD03] text-black px-3 py-1 text-xs font-semibold backdrop-blur-md">
                      <Check className="h-3 w-3" /> Unlocked
                    </span>
                  ) : free ? (
                    <span data-testid={`tag-${v.id}`} className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-[#00F0FF] text-black px-3 py-1 text-xs font-semibold backdrop-blur-md">
                      <Gift className="h-3 w-3" /> Free
                    </span>
                  ) : (
                    <span data-testid={`tag-${v.id}`} className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/60 text-white px-3 py-1 text-xs backdrop-blur-md border border-white/10">
                      <Lock className="h-3 w-3" /> ${v.price.toFixed(2)}
                    </span>
                  )}
                </button>

                <div className="p-4">
                  <h3 className="font-medium truncate">{v.title}</h3>
                  <div className="mt-3">
                    {owned ? (
                      <button
                        onClick={() => download(v)}
                        data-testid={`download-clip-${v.id}`}
                        className="w-full inline-flex items-center justify-center gap-2 bg-[#FFDD03] text-black font-semibold rounded-full py-2.5 text-sm hover:scale-[1.02] transition-transform duration-300"
                      >
                        <Download className="h-4 w-4" /> Download original
                      </button>
                    ) : free ? (
                      <button
                        onClick={() => download(v)}
                        data-testid={`download-clip-${v.id}`}
                        className="w-full inline-flex items-center justify-center gap-2 bg-[#00F0FF] text-black font-semibold rounded-full py-2.5 text-sm hover:scale-[1.02] transition-transform duration-300"
                      >
                        <Download className="h-4 w-4" /> Download original
                      </button>
                    ) : canFree ? (
                      <button
                        onClick={() => unlockFree(v)}
                        disabled={busyId === v.id}
                        data-testid={`unlock-clip-${v.id}`}
                        className="w-full inline-flex items-center justify-center gap-2 bg-[#00F0FF] text-black font-semibold rounded-full py-2.5 text-sm hover:scale-[1.02] transition-transform duration-300 disabled:opacity-60"
                      >
                        {busyId === v.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
                        Use free download
                      </button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => buy(v)}
                          disabled={busyId === v.id}
                          data-testid={`buy-clip-${v.id}`}
                          className="flex-1 inline-flex items-center justify-center gap-2 bg-[#00F0FF] text-black font-semibold rounded-full py-2.5 text-sm hover:scale-[1.02] transition-transform duration-300 disabled:opacity-60"
                        >
                          {busyId === v.id ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                          Buy ${v.price.toFixed(2)}
                        </button>
                        <button
                          onClick={() => toggleCart(v)}
                          data-testid={`cart-clip-${v.id}`}
                          title={inCart(v) ? "Remove from cart" : "Add to cart"}
                          className={`shrink-0 inline-flex items-center justify-center gap-2 rounded-full py-2.5 px-4 text-sm font-semibold border transition-colors ${
                            inCart(v)
                              ? "bg-[#FFDD03] text-black border-[#FFDD03]"
                              : "bg-transparent text-white border-white/20 hover:border-white/40 hover:bg-white/5"
                          }`}
                        >
                          {inCart(v) ? <Check className="h-4 w-4" /> : <ShoppingCart className="h-4 w-4" />}
                          {inCart(v) ? "Added" : "Add"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {vault?.videos.length === 0 && (
          <div className="text-center py-20 text-white/40">No clips in this vault yet.</div>
        )}
      </section>

      {/* Sticky cart bar */}
      {cartItems.length > 0 && (
        <div
          data-testid="cart-bar"
          className="fixed bottom-0 inset-x-0 z-50 border-t border-white/10 bg-[#0d0d0d]/95 backdrop-blur-md"
        >
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <span className="grid place-items-center h-10 w-10 rounded-xl bg-[#FFDD03]/15 text-[#FFDD03] shrink-0">
                <ShoppingCart className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white" data-testid="cart-count">
                  {cartItems.length} clip{cartItems.length > 1 ? "s" : ""} in cart
                </p>
                <p className="text-xs text-white/50 truncate">
                  Total <span className="text-[#FFDD03] font-semibold">${cartTotal.toFixed(2)}</span> · clear anytime
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setCart(new Set())}
                data-testid="cart-clear-btn"
                className="hidden sm:inline-flex text-sm text-white/50 hover:text-white transition-colors px-3 py-2"
              >
                Clear
              </button>
              <button
                onClick={checkoutCart}
                disabled={cartBusy}
                data-testid="cart-checkout-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#FFDD03] text-black font-semibold rounded-full px-6 py-2.5 text-sm hover:scale-105 transition-transform duration-300 disabled:opacity-60 disabled:hover:scale-100"
              >
                {cartBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
                Checkout ${cartTotal.toFixed(2)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Player modal */}
      <Dialog open={!!player} onOpenChange={(o) => !o && setPlayer(null)}>
        <DialogContent className="max-w-4xl bg-black border-white/10 p-0 overflow-hidden">
          <button
            onClick={() => setPlayer(null)}
            className="absolute top-3 right-3 z-20 grid place-items-center h-9 w-9 rounded-full bg-black/60 backdrop-blur-md text-white hover:bg-white/20 transition-colors"
            data-testid="close-player-btn"
          >
            <X className="h-4 w-4" />
          </button>
          {player && (
            <div>
              <DialogTitle className="sr-only">{player.title}</DialogTitle>
              <DialogDescription className="sr-only">Video preview for {player.title}</DialogDescription>
              <div className="relative">
                <video
                  key={player.id}
                  data-testid="video-player"
                  src={`${API}/vault/${slug}/videos/${player.id}/stream`}
                  controls
                  autoPlay
                  className="w-full aspect-video bg-black"
                />
                <span
                  data-testid="preview-quality-badge"
                  className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/60 backdrop-blur-md text-white/85 text-xs font-medium px-2.5 py-1 ring-1 ring-white/10"
                >
                  <Sparkles className="h-3.5 w-3.5 text-[#00F0FF]" />
                  Preview quality
                </span>
                {playerIndex > 0 && (
                  <button
                    onClick={() => gotoPlayer(-1)}
                    data-testid="player-prev-btn"
                    aria-label="Previous clip"
                    className="absolute left-3 top-1/2 -translate-y-1/2 z-10 grid place-items-center h-11 w-11 rounded-full bg-black/60 backdrop-blur-md text-white hover:bg-[#00F0FF] hover:text-black transition-colors"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                )}
                {vault && playerIndex < vault.videos.length - 1 && (
                  <button
                    onClick={() => gotoPlayer(1)}
                    data-testid="player-next-btn"
                    aria-label="Next clip"
                    className="absolute right-3 top-1/2 -translate-y-1/2 z-10 grid place-items-center h-11 w-11 rounded-full bg-black/60 backdrop-blur-md text-white hover:bg-[#00F0FF] hover:text-black transition-colors"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                )}
                {vault && (
                  <span data-testid="player-counter" className="absolute bottom-3 left-3 z-10 rounded-full bg-black/60 backdrop-blur-md text-white/80 text-xs px-2.5 py-1">
                    {playerIndex + 1} / {vault.videos.length}
                  </span>
                )}
              </div>
              <div className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h3 className="font-display text-lg font-semibold">{player.title}</h3>
                  <p data-testid="quality-hint" className="text-xs text-white/40 mt-0.5 flex items-center gap-1.5">
                    <Sparkles className="h-3 w-3 text-[#00F0FF]/70" />
                    You're watching a lighter preview · download for full quality
                  </p>
                </div>
                <div className="shrink-0">
                  {isOwned(player) ? (
                    <button
                      onClick={() => download(player)}
                      data-testid="modal-download-btn"
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#FFDD03] text-black font-semibold rounded-full px-6 py-2.5 text-sm hover:scale-[1.02] transition-transform duration-300"
                    >
                      <Download className="h-4 w-4" /> Download original
                    </button>
                  ) : isFree(player) ? (
                    <button
                      onClick={() => download(player)}
                      data-testid="modal-download-btn"
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#00F0FF] text-black font-semibold rounded-full px-6 py-2.5 text-sm hover:scale-[1.02] transition-transform duration-300"
                    >
                      <Download className="h-4 w-4" /> Download original
                    </button>
                  ) : remaining > 0 ? (
                    <button
                      onClick={() => unlockFree(player)}
                      disabled={busyId === player.id}
                      data-testid="modal-unlock-btn"
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#00F0FF] text-black font-semibold rounded-full px-6 py-2.5 text-sm hover:scale-[1.02] transition-transform duration-300 disabled:opacity-60"
                    >
                      {busyId === player.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
                      Use free download
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => buy(player)}
                        disabled={busyId === player.id}
                        data-testid="modal-buy-btn"
                        className="inline-flex items-center justify-center gap-2 bg-[#00F0FF] text-black font-semibold rounded-full px-6 py-2.5 text-sm hover:scale-[1.02] transition-transform duration-300 disabled:opacity-60"
                      >
                        {busyId === player.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
                        Buy for ${player.price.toFixed(2)}
                      </button>
                      <button
                        onClick={() => toggleCart(player)}
                        data-testid="modal-cart-btn"
                        className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold border transition-colors ${
                          inCart(player)
                            ? "bg-[#FFDD03] text-black border-[#FFDD03]"
                            : "bg-transparent text-white border-white/20 hover:border-white/40 hover:bg-white/5"
                        }`}
                      >
                        {inCart(player) ? <Check className="h-4 w-4" /> : <ShoppingCart className="h-4 w-4" />}
                        {inCart(player) ? "In cart" : "Add to cart"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
